import { Box, Typography } from '@mui/material'
import NodeBox from './common/NodeBox'
import Crm from '@assets/flowchart/Crm.svg'
import NodeInput from './common/NodeInput'
import NodeSelect from './common/NodeSelect'
import PermIdentityRoundedIcon from '@mui/icons-material/PermIdentityRounded'
import { useNodeData } from './common/useNodeData'
import { useReference } from '../ReferenceContext'
import { useCrmAccountId, useCrmReference } from '@/hooks/useCrm'
import {
  CRM_OPEN_FOR_UNIT_OPTIONS,
  legacyCrmTaskType,
} from '@/utils/treeSerializer'

/**
 * Node data keys: `taskType` (task type name), `assigneeId` (CRM assignee id,
 * as a string), `firstSelectedId` (open-for unit option id) and `firstValue`
 * (open-for amount). Trees saved before these keys existed carry the task type
 * in `secondSelectedId` as an id of the old hard-coded list.
 */
const CrmNode = ({ id, data }: { id: string; data: any }) => {
  const patch = useNodeData(id)
  const { crmTaskTypes } = useReference()
  const { data: crmReference } = useCrmReference(useCrmAccountId())

  const taskType: string | null =
    data?.taskType ?? legacyCrmTaskType(data?.secondSelectedId) ?? null
  const taskTypeOptions = crmTaskTypes.map(name => ({ id: name, label: name }))
  // Keep a saved type visible even if it is no longer in the reference list.
  if (taskType && !crmTaskTypes.includes(taskType)) {
    taskTypeOptions.push({ id: taskType, label: taskType })
  }
  const assigneeOptions = (crmReference?.assignees ?? []).map(a => ({
    id: String(a.id),
    label: a.name,
  }))

  return (
    <NodeBox title="CRM" icon={Crm} nodeId={id}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <NodeSelect
          placeholder="מאפיין"
          options={CRM_OPEN_FOR_UNIT_OPTIONS}
          value={data?.firstSelectedId ?? null}
          onChangeValue={v => patch({ firstSelectedId: v })}
        />
        <NodeInput
          value={data?.firstValue ?? ''}
          onChange={v => patch({ firstValue: v })}
        />
        <Typography
          sx={{
            color: '#003F61',
            textAlign: 'right',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '100%',
          }}
        >
          התראה פתוחה למשך
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginTop: '24px',
        }}
      >
        <NodeSelect
          placeholder="מאפיין"
          options={assigneeOptions}
          Icon={PermIdentityRoundedIcon}
          value={data?.assigneeId ?? null}
          onChangeValue={v => patch({ assigneeId: v })}
        />
        <Typography
          sx={{
            color: '#003F61',
            textAlign: 'right',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '100%',
          }}
        >
          ושיוך ל
        </Typography>
        <NodeSelect
          placeholder="מאפיין"
          options={taskTypeOptions}
          value={taskType}
          onChangeValue={v => patch({ taskType: v, secondSelectedId: null })}
        />
        <Typography
          sx={{
            color: '#003F61',
            textAlign: 'right',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '100%',
          }}
        >
          פתיחת משימה מסוג{' '}
        </Typography>
      </Box>
    </NodeBox>
  )
}

export default CrmNode
